import { useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Close, RoomService, Update } from "@mui/icons-material";
import { BookingObjectFavoriteButton } from "./booking-object-favorite-button.component";
import { useSelector } from "../../../../../../../app/helpers";
import { RootState } from "../../../../../../../app/rootReducer";
import { IPlaceSchedule } from "../../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../../Domain/Types/FloorPlan/ZoneSchedule";
import ImageIcon from "@mui/icons-material/Image";
import { useTranslation } from "react-i18next";
import { useRemoteFetchZoneInventory } from "../../../../../../../hooks/Remote/Inventory/useRemoteFetchZoneInventoryMutation";
import { EnlargedImageCarouselDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-carousel-dialog.component";
import { BookingType } from "../../../../../../../features/Booking-Form/typings/booking-inputs";

type P = {
  isConferenceZone: boolean;
  isOccupiedDataLoading: boolean;
  isMandatoryForService: boolean;
  isServiceExisting: boolean;
  isServiceAdminBypass?: boolean;
  /**
   * When set (e.g. SelectionPrompt2), opening services is allowed only if there is something to act on:
   * at least one orderable item (after time rules) or an existing conference order for this inventory.
   * When omitted, legacy behavior: enabled if {@link isServiceExisting} or {@link isServiceAdminBypass}.
   */
  isConferenceServicesActionEnabled?: boolean;
  /** When mandatory services apply: true only if required catering/equipment/layout selections are done */
  mandatoryServicesFulfilled?: boolean;
  isBookingServiceLoading: boolean;
  selectedEntry: IPlaceSchedule | IZoneSchedule;
  openTimeDialog: () => void;
  setIsServiceModalOpen?: (o: boolean) => void;
  onClose: (e: any) => void;
  /** Current floor; with favoriteBookingType enables favorites toggle for supported modes */
  favoriteFloorInventoryId?: number;
  favoriteBookingType?: BookingType | null;
};
export const SelectionPromptHeader: React.FC<P> = props => {
  const {
    isConferenceZone,
    isOccupiedDataLoading,
    isMandatoryForService,
    isServiceExisting,
    isServiceAdminBypass = false,
    isConferenceServicesActionEnabled,
    mandatoryServicesFulfilled,
    isBookingServiceLoading,
    selectedEntry,
    openTimeDialog,
    setIsServiceModalOpen,
    onClose,
    favoriteFloorInventoryId,
    favoriteBookingType
  } = props;

  const { t } = useTranslation();
  const { conferenceService } = useSelector((state: RootState) => state.booking.inputs);

  const [roomImagesDialogOpen, setRoomImagesDialogOpen] = useState(false);
  const [roomPicturePaths, setRoomPicturePaths] = useState<string[] | null>(null);

  const { mutateAsync: fetchZoneInventory, isLoading: fetchZoneInventoryLoading } =
    useRemoteFetchZoneInventory();

  const handleOpenRoomImagesDialog = () => {
    setRoomImagesDialogOpen(true);

    const zoneInventoryId = selectedEntry?.inventory?.id;
    if (!zoneInventoryId) return;

    fetchZoneInventory(zoneInventoryId)
      .then(data => setRoomPicturePaths(data.picturePaths ?? []))
      .catch(() => setRoomPicturePaths([]));
  };

  const serviceIconColor = useMemo(() => {
    const entryId = selectedEntry?.inventory?.id || selectedEntry?.inventoryId;

    // sanity check
    if (!entryId) return "default";

    // Mandatory: only error when services are orderable; then error until required selections are fulfilled (or legacy: Redux entry only if mandatoryServicesFulfilled omitted)
    if (isMandatoryForService) {
      if (!isServiceExisting) return "default";
      const mandatoryComplete =
        mandatoryServicesFulfilled === undefined
          ? !!conferenceService?.[entryId]
          : mandatoryServicesFulfilled;
      if (!mandatoryComplete) return "error";
      return "info";
    }

    // no service ordered yet
    if (!conferenceService?.[entryId]) return "default";

    // at least one of the services is ordered
    const selectedConfService = conferenceService[entryId];
    if (selectedConfService?.caterings?.some(cat => cat.qty > 0)) return "info";
    if (selectedConfService?.bookableEquipments?.length > 0) return "info";
    if (selectedConfService?.roomLayout?.length > 0) return "info";

    return "default";
  }, [
    selectedEntry,
    conferenceService,
    isMandatoryForService,
    isServiceExisting,
    mandatoryServicesFulfilled
  ]);

  const handleClose = () => {
    setRoomImagesDialogOpen(false);
  };

  const handleRoomImagesDialogExited = () => {
    setRoomPicturePaths(null);
  };

  const isServicesButtonEnabled = useMemo(() => {
    if (isConferenceServicesActionEnabled !== undefined) {
      return isConferenceServicesActionEnabled;
    }
    return isServiceExisting || isServiceAdminBypass;
  }, [isConferenceServicesActionEnabled, isServiceExisting, isServiceAdminBypass]);

  const bookingInventoryId = selectedEntry?.inventory?.id ?? selectedEntry?.inventoryId ?? 0;

  return (
    <>
      <Box
        data-testid="selection-prompt-header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        {/* === ACTION BAR === */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          {/* LEFT: context actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isConferenceZone && (
              <Tooltip title={<Typography>{t("Show Images")}</Typography>}>
                <IconButton data-testid="show-images-btn" onClick={handleOpenRoomImagesDialog}>
                  <ImageIcon />
                </IconButton>
              </Tooltip>
            )}

            {setIsServiceModalOpen && (
              <Tooltip title={<Typography>{t("Show Services")}</Typography>}>
                <IconButton
                  loading={isBookingServiceLoading}
                  disabled={!isServicesButtonEnabled}
                  onClick={() => setIsServiceModalOpen(true)}
                  data-testid="booking-services-btn"
                  color={serviceIconColor}
                  sx={{
                    ...(serviceIconColor === "error" &&
                      isServiceExisting && {
                        animation: "rotateShake 0.3s ease-in-out 3"
                      }),
                    "@keyframes rotateShake": {
                      "0%": { transform: "rotate(0deg)" },
                      "20%": { transform: "rotate(-10deg)" },
                      "40%": { transform: "rotate(10deg)" },
                      "60%": { transform: "rotate(-10deg)" },
                      "80%": { transform: "rotate(10deg)" },
                      "100%": { transform: "rotate(0deg)" }
                    }
                  }}
                >
                  <RoomService />
                </IconButton>
              </Tooltip>
            )}

            {/* allow to change schedule time */}
            <Tooltip title={<Typography>{t("Change time")}</Typography>}>
              <span>
                <IconButton
                  data-testid="change-time"
                  loading={isOccupiedDataLoading}
                  onClick={openTimeDialog}
                  disabled={isOccupiedDataLoading}
                >
                  <Update />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* RIGHT: user actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <BookingObjectFavoriteButton
              bookingType={favoriteBookingType}
              bookingInventoryId={bookingInventoryId}
              floorInventoryId={favoriteFloorInventoryId}
            />

            <IconButton data-testid="close-prompt-btn" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* === TITLE === */}
      <Typography
        variant="h6"
        sx={{
          my: 1,
          fontWeight: 600,
          lineHeight: 1.3
        }}
      >
        {selectedEntry.inventory?.name}
      </Typography>

      <EnlargedImageCarouselDialog
        isOpen={roomImagesDialogOpen}
        handleClose={handleClose}
        picturePaths={roomPicturePaths}
        isLoading={fetchZoneInventoryLoading}
        onDialogExited={handleRoomImagesDialogExited}
      />
    </>
  );
};
